
import get from 'lodash/get';
import PDPCommentBody from './PDPCommentBody.vue';
import PDPCommentButtons from './PDPCommentButtons.vue';
import PDPCommentControls from './PDPCommentControls.vue';
import PDPCommentRatings from './PDPCommentRatings.vue';
import PDPCommentStatusTag from './PDPCommentStatusTag.vue';
import PDPUserProfile from './PDPUserProfile.vue';
import DefaultAvatar from '../../../../static/images/profile/as-profile-default-avatar.svg';

export default {
  name: 'PDPCommentNode',
  components: {
    PDPCommentBody,
    PDPCommentButtons,
    PDPCommentControls,
    PDPCommentRatings,
    PDPCommentStatusTag,
    PDPUserProfile,
  },
  props: {
    allowFlagging: {
      type: Boolean,
      default: false,
    },
    filterComments: {
      type: Boolean,
      default: false,
    },
    // jw: this is the discussion that the comment is part of.
    discussion: {
      type: Object,
      required: true,
    },
    // jw: this is the actual comment itself, and for root level comments it will be the same as
    //     the discussion.
    comment: {
      type: Object,
      required: true,
    },
    commentTypeTitle: {
      type: String,
      required: true,
    },
    dealPartnerUserIds: {
      type: Array,
      required: true,
    },
    isPinned: {
      type: Boolean,
    },
    positionIndices: {
      type: Array,
      default: () => ([]),
    },
    forceShowAll: {
      type: Boolean,
    },
    highlightComment: {
      type: String,
      default: '',
    },
    // jw: this property is only relevant to parent comments and is always provided in that case.
    //     Replies do not support expand/collapse so I am defaulting this to null since neither
    //     state is relevant.
    showAllChildren: {
      type: Boolean,
      default: null,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    forReviews: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      approvedStatus: this.comment.approved,
      blacklistStatus: this.comment.user.comment_blacklist,
      pinnedStatus: this.comment.pinned,
      resolvedStatus: this.comment.resolved,
      upvoteCount: this.comment.num_likes,
      whitelistStatus: this.comment.user.comment_whitelist,
      // jw: default show children to the showAllChildren flag, otherwise we will render in conflict
      //     with the visible toggle.
      showChildren: this.showAllChildren,
      titleIsClamped: false,
    };
  },
  inject: {
    deal: {},
    forDiscussionPage: {
      default: false,
    },
  },
  computed: {
    noReply() {
      return this.comment.level === 2;
    },
    helpfulBtnStartingState() {
      return !!this.comment.likedit_user_ids?.includes(this.user.id);
    },
    commentTitle() {
      const title = this.comment.title || this.comment.comment;
      if (this.forDiscussionPage && this.forReviews) {
        return `${this.deal.public_name} Review: ${title}`;
      }
      return title;
    },
    prefixForId() {
      if (this.forReviews) {
        return 'r';
      }
      return 'q';
    },
    supportsDiscussionPages() {
      return !!this.deal.render_nuxt_pdp.support_discussion_pages;
    },
    isDiscussion() {
      return this.discussion.id === this.comment.id; // root element or level 0
    },
    idForLink() {
      // jw: do not include the prefix for id when we are linking to the discussion page.
      if (this.supportsDiscussionPages) {
        return this.comment.id;
      }
      return `${this.prefixForId}${this.comment.id}`;
    },
    fragmentForLink() {
      // jw: always include the fragment in the link if discussion pages are not supported, or if
      //     this is a reply.
      if (!this.supportsDiscussionPages || !this.isDiscussion) {
        return this.idForLink;
      }
      return null;
    },
    discussionDisplayPath() {
      if (this.supportsDiscussionPages) {
        return this.discussion.display_path;
      }

      return this.deal.get_absolute_url;
    },
    avatarSrc() {
      const avatar = get(this, ['comment', 'user', 'avatar']);

      // jl: using optional chaining here breaks test case (for the time being)
      if (!avatar || !avatar.includes('/media/')) {
        // jl: user has not uploaded their own profile pic, override default gravatar
        return `${DefaultAvatar}?width=100&height=100&aspect_ratio=1:1`;
      }

      const queryParams = {};
      const [url, params = ''] = avatar.split('?');

      // Retain all existing params
      if (params.length) {
        const pairs = params.split('&');

        pairs.forEach((pair) => {
          const [key, value] = pair.split('=');
          queryParams[decodeURIComponent(key)] = decodeURIComponent(value || '');
        });
      }

      // Override (or add to) any existing params
      queryParams.width = 100;
      queryParams.height = 100;
      queryParams.aspect_ratio = '1:1';

      const keys = Object.keys(queryParams);
      let newUrl = `${url}?`;

      keys.forEach((key, index) => {
        newUrl += `${index !== 0 ? '&' : ''}${key}=${queryParams[key]}`;
      });

      return newUrl;
    },
    commentCount() {
      return this.isDiscussion ? this.calcCount(this.comment) - 1 : 0;
    },
    pinnedClass() {
      // todo:LED-1136: We need to support this for questions eventually.
      return (this.pinnedStatus && this.forReviews) ? 'comment-status-pinned' : '';
    },
    commenterIsDealPartner() {
      return this.dealPartnerUserIds.includes(this.comment.user.id);
    },
    statusOutlineClass() {
      if (this.user.isModerator) {
        if (typeof this.approvedStatus === 'undefined' || this.approvedStatus === null) {
          return 'comment-status-unmoderated';
        }

        if (!this.approvedStatus) {
          return 'comment-status-moderated';
        }

        if (!this.resolvedStatus) {
          return 'comment-status-unresolved';
        }
      }

      if (!this.isDiscussion) {
        if (this.commenterIsDealPartner) {
          return 'comment-status-creator';
        }
        return 'comment-status-active';
      }

      return '';
    },
    showModMessage() {
      return (
        typeof this.approvedStatus === 'undefined'
          || this.approvedStatus === null
          || !this.approvedStatus
      )
        && this.user.id === this.comment.user.id;
    },
    childrenLength() {
      return (this.comment.children && this.comment.children.length) || 0;
    },
  },
  watch: {
    showAllChildren(val) {
      this.onClickShowHide(val);
    },
    forceShowAll(val) {
      this.onClickShowHide(val);
    },
    comment() {
      // After comment data refresh, reset statuses
      this.approvedStatus = this.comment.approved;
      this.blacklistStatus = this.comment.user.comment_blacklist;
      this.pinnedStatus = this.comment.pinned;
      this.resolvedStatus = this.comment.resolved;
      this.whitelistStatus = this.comment.user.comment_whitelist;
    },
    childrenLength(newLength, oldLength) {
      // jw: if the number of replies within this node grows we want to show the children. This
      //     should only happen when a reply is made by the current user.
      if (this.isDiscussion && newLength > oldLength) {
        // jw: if this is the first reply we need to force a render so that the height will be setup
        //     since it never was before.
        this.onClickShowHide(true, newLength === 1);
      }
    },
  },
  mounted() {
    const { sliderParent, sliderChild, commentTitle } = this.$refs;

    if (typeof window !== 'undefined' && !this.forReviews && commentTitle) {
      // Only for Questions: display body copy if discussion is too long and is clamped by CSS
      this.titleIsClamped = commentTitle.scrollHeight > commentTitle.clientHeight;
    }

    if (!this.forceShowAll || !sliderParent || !sliderChild) {
      return;
    }

    this.setSliderHeightAndFollow(sliderParent, sliderChild.clientHeight);
  },
  methods: {
    /* eslint-disable no-param-reassign */
    setSliderHeightAndFollow(sliderElement, newHeight) {
      sliderElement.style.height = `${newHeight}px`;

      // Hack to make sure any future height changes are adjusted (animation is 250ms)
      setTimeout(() => {
        sliderElement.style.height = 'auto';
      }, 300);
    },
    /* eslint-enable no-param-reassign */
    calcCount(comment) {
      let count = 1;
      if (comment && comment.children) {
        for (let i = 0; i < comment.children.length; i += 1) {
          count += this.calcCount(comment.children[i]);
        }
      }

      return count;
    },
    updateState(data) {
      const { type, value, error } = data;

      if (error) {
        this.$toastr.e('Something went wrong');
        return;
      }

      switch (type) {
        case 'approved':
          this.approvedStatus = value;
          break;
        case 'resolved':
          this.resolvedStatus = value;
          break;
        case 'upvote':
          this.upvoteCount = value;
          return; // jl: let's not display 'success' toast for upvote, as UI is self-explanatory
        case 'whitelist':
          this.whitelistStatus = value;
          break;
        case 'blacklist':
          this.blacklistStatus = value;
          break;
        case 'pinned':
          this.pinnedStatus = value;
          break;
        default:
          break;
      }

      this.$toastr.s('Success');
    },
    replyComment() {
      this.$emit('reply-comment', {
        id: this.comment.id,
        positionIndices: this.positionIndices,
        level: this.comment.level,
      });
    },
    replyChildComment(data) {
      this.$emit('reply-comment', data);
    },
    updateAction(data) {
      this.$emit('update-action', {
        ...data,
        id: this.comment.id,
        commentUserId: get(this, ['comment', 'user', 'id']),
        done: this.updateState,
      });
    },
    updateChildAction(data) {
      this.$emit('update-action', data);
    },
    updateIndex(index) {
      const updatedIndices = [...this.positionIndices];
      updatedIndices.push(index);

      return updatedIndices;
    },
    onClickShowHide(val, forceRender = false) {
      // jw: if the provided value matches we can short out. Note: we are doing explicit matches
      //     meaning that the link which provides an event object will not match here and will fall
      //     through.
      if (val === this.showChildren && !forceRender) {
        return;
      }

      this.$nextTick(() => {
        const { sliderParent, sliderChild } = this.$refs;

        // jw: if val is not a boolean then it will be a click event object for the link from the
        //     page, in which case we want to treat this like a toggle.
        if (typeof val === 'boolean') {
          this.showChildren = val;
        } else {
          this.showChildren = !this.showChildren;
        }

        // jw: it is vital that we short out prior to performing animations, but after we establish
        //     the appropriate value above. This way when replies are made we will be testing
        //     against the intended state of the component.
        if (!sliderParent || !sliderChild) {
          return;
        }

        if (!this.showChildren) {
          sliderParent.style.height = `${sliderChild.clientHeight}px`;

          setTimeout(() => {
            sliderParent.style.height = '0';
          }, 50);
        } else {
          this.setSliderHeightAndFollow(sliderParent, sliderChild.clientHeight);
        }
      });
    },
  },
};
